import { RxWithTherapistsDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from 'react';
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

const useRxTherapistCountStats = (startDate: Date, endDate: Date, locationIds?: string[], therapistCountFilter?: number[] | 'all', rxType?: string) => {
  const BIStore = useStore(BIContext); 
  const { getRxTherapistsStats } = BIStore; 

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allRxTherapistsStats, setAllRxTherapistsStats] = useState<RxWithTherapistsDto[]>([]);
  // Store filtered stats per selected locations
  const [rxTherapistsStats, setRxTherapistsStats] = useState<RxWithTherapistsDto[]>([]);
  const [filteredRxTherapistsStats, setFilteredRxTherapistsStats] = useState<RxWithTherapistsDto[]>([]);
  const [availableTherapistCounts, setAvailableTherapistCounts] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        let response: RxWithTherapistsDto[] | undefined;
        if (debounceStartDate && debounceEndDate) {
          // console.log(`[Rx Therapist Count] Fetching data for date range: ${debounceStartDate} - ${debounceEndDate}`);
          // Always fetch all locations data, don't pass location filter to backend
          response = await getRxTherapistsStats(debounceStartDate, debounceEndDate, undefined, rxType);
          // console.log("[Rx Therapist Count] Backend responded with:", response);
        } else {
          // console.log("[Rx Therapist Count] Fetching all data");
        }
        
        if (response && response.length > 0) {
          setAllRxTherapistsStats(response);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setRxTherapistsStats(response.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setRxTherapistsStats(response);
          }

          // Calculate available therapist counts
          const uniqueCounts = [...new Set(response.map(item => item.therapistCount))].sort((a, b) => a - b);
          setAvailableTherapistCounts(uniqueCounts);
        } else {
          // console.log("[Rx Therapist Count] No data found for the specified range.");
          setAllRxTherapistsStats([]);
          setRxTherapistsStats([]);
        }
      } catch (error) {
        // console.log("[Rx Therapist Count] Error fetching Rx stats: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debounceStartDate, debounceEndDate, getRxTherapistsStats, rxType]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allRxTherapistsStats.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setRxTherapistsStats(allRxTherapistsStats.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setRxTherapistsStats(allRxTherapistsStats);
      }
    }
  }, [memoizedLocationIds, allRxTherapistsStats, isLoading]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allRxTherapistsStats.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allRxTherapistsStats]);

  // Filter therapist stats based on therapist count filter
  useEffect(() => {
    if (therapistCountFilter === 'all' || therapistCountFilter === undefined || therapistCountFilter.length === 0) {
      setFilteredRxTherapistsStats(rxTherapistsStats);
    } else {
      const filtered = rxTherapistsStats.filter(stat => therapistCountFilter.includes(stat.therapistCount));
      setFilteredRxTherapistsStats(filtered);
    }
  }, [rxTherapistsStats, therapistCountFilter]);

  return { rxTherapistsStats, isLoading, availableLocations, filteredRxTherapistsStats, availableTherapistCounts, };
};

export default useRxTherapistCountStats;
